import { useState } from 'react';
import { API, useAuth } from '@/App';
import { useWallet } from '@/components/TopNav';
import axios from 'axios';
import { X, ArrowRightLeft, Loader2, Search, Users, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function SendCreditsModal({ open, onClose }) {
  const { user } = useAuth();
  const { balance, creditLimit, availableCredit, refresh } = useWallet();
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);

  if (!open) return null;

  const handleSend = async () => {
    if (!recipient.trim()) return toast.error('Enter recipient email or user ID');
    const amt = parseInt(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (amt > availableCredit) return toast.error(`Insufficient credits. Available: ${availableCredit} cr`);

    setSending(true);
    try {
      const res = await axios.post(`${API}/transfers/send`, {
        to: recipient.trim(),
        amount: amt,
        description: description.trim() || 'Credit transfer',
      });
      setSuccess(res.data);
      refresh();
      toast.success(`Sent ${amt} cr to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setRecipient('');
    setAmount('');
    setDescription('');
    setSuccess(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-testid="send-credits-modal">
        {/* Header */}
        <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Send Credits</h2>
              <p className="text-[11px] text-gray-400">Transfer credits to another user</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="close-send-modal">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4">
          {success ? (
            /* Success State */
            <div className="text-center py-6" data-testid="transfer-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Check className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Transfer Complete</h3>
              <p className="text-sm text-gray-500 mt-1">
                Sent <span className="font-bold text-gray-900">{success.amount?.toLocaleString()} cr</span> to <span className="font-bold text-gray-900">{success.to_name || success.to}</span>
              </p>
              <p className="text-xs text-gray-400 mt-2">New balance: {success.new_balance?.toLocaleString()} cr</p>
              <button onClick={handleClose} className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-gray-800" data-testid="transfer-done-btn">
                Done
              </button>
            </div>
          ) : (
            /* Transfer Form */
            <div className="space-y-4">
              {/* Balance Info */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-500 font-medium">Available to send</span>
                <span className="text-lg font-black text-gray-900">{availableCredit.toLocaleString()} <span className="text-xs font-semibold text-gray-400">cr</span></span>
              </div>

              {balance < 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                  <p className="text-[11px] text-amber-700 font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Your balance is negative ({balance} cr). Transfers will use your credit line.
                  </p>
                </div>
              )}

              {/* Recipient */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Recipient</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    value={recipient}
                    onChange={e => setRecipient(e.target.value)}
                    placeholder="Email address or User ID"
                    className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="transfer-recipient"
                  />
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Amount (credits)</label>
                <input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="e.g. 100"
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="transfer-amount"
                />
                {amount && parseInt(amount) > 0 && (
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-[10px] text-gray-400">Remaining after transfer: {(availableCredit - parseInt(amount || 0)).toLocaleString()} cr</p>
                    {parseInt(amount) > availableCredit && <p className="text-[10px] text-red-500 font-semibold">Exceeds available</p>}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Note (optional)</label>
                <input
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="e.g. Payment for course fees"
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="transfer-description"
                />
              </div>

              {/* Send Button */}
              <button
                onClick={handleSend}
                disabled={sending || !recipient.trim() || !amount || parseInt(amount) <= 0 || parseInt(amount) > availableCredit}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="send-credits-btn"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <ArrowRightLeft className="h-4 w-4" />
                    Send {amount && parseInt(amount) > 0 ? `${parseInt(amount).toLocaleString()} cr` : 'Credits'}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
